/**
 * Auto-Renewal Subscription Helper Functions
 * 
 * These functions help users set up and manage auto-renewal subscriptions
 * by handling token delegation to the contract's PDA.
 */

import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
} from "@solana/web3.js"
import {
  createApproveInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
  getAccount,
} from "@solana/spl-token"
import { IRMA_PROGRAM_ID } from "./irma-client-browser"

/**
 * Derive the auto-renewal delegate PDA for a subscriber
 */
export function getAutoRenewDelegatePDA(
  subscriber: PublicKey
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from("auto_renew_delegate"),
      subscriber.toBuffer(),
    ],
    IRMA_PROGRAM_ID
  )
}

/**
 * Check if auto-renewal delegation is approved
 */
export async function checkDelegationApproved(
  connection: Connection,
  subscriber: PublicKey,
  mint: PublicKey,
  delegatePDA: PublicKey
): Promise<{ approved: boolean; amount: number }> {
  try {
    const subscriberATA = await getAssociatedTokenAddress(mint, subscriber)
    const account = await getAccount(connection, subscriberATA)
    
    if (account.delegate && account.delegate.equals(delegatePDA)) {
      return {
        approved: true,
        amount: Number(account.delegatedAmount),
      }
    }
    
    return { approved: false, amount: 0 }
  } catch (error) {
    return { approved: false, amount: 0 }
  }
}

/**
 * Approve token delegation for auto-renewal
 * 
 * This allows the contract's PDA to automatically transfer tokens
 * from the user's account when renewing subscriptions.
 * 
 * @param connection - Solana connection
 * @param subscriber - User's wallet public key
 * @param mint - IRMA token mint address
 * @param amount - Amount to delegate (should be subscription price + buffer)
 * @param wallet - User's wallet for signing
 */
export async function approveAutoRenewalDelegation(
  connection: Connection,
  subscriber: PublicKey,
  mint: PublicKey,
  amount: number,
  wallet: {
    publicKey: PublicKey
    signTransaction: (tx: Transaction) => Promise<Transaction>
  }
): Promise<string> {
  // Get subscriber's token account
  const subscriberATA = await getAssociatedTokenAddress(mint, subscriber)
  
  // Derive delegate PDA
  const [delegatePDA] = getAutoRenewDelegatePDA(subscriber)
  
  // Check current delegation
  const currentDelegation = await checkDelegationApproved(
    connection,
    subscriber,
    mint,
    delegatePDA
  )
  
  // If already delegated with sufficient amount, skip
  if (currentDelegation.approved && currentDelegation.amount >= amount) {
    console.log("✅ Delegation already approved with sufficient amount")
    return "already_approved"
  }
  
  // Approve delegation
  // Add 10% buffer for price changes
  const delegateAmount = BigInt(Math.ceil(amount * 1.1))
  
  console.log("🔐 Approving auto-renewal delegation...")
  console.log("   Subscriber:", subscriber.toString())
  console.log("   Delegate PDA:", delegatePDA.toString())
  console.log("   Amount:", delegateAmount.toString())
  
  // Build the approve instruction manually and sign with wallet adapter
  // The approve function from SPL token expects a Keypair, but we have a wallet adapter
  // So we'll build the transaction manually
  const approveInstruction = createApproveInstruction(
    subscriberATA,
    delegatePDA,
    wallet.publicKey,
    delegateAmount,
    [],
    TOKEN_PROGRAM_ID
  )

  // Build and sign transaction
  const transaction = new Transaction().add(approveInstruction)
  
  const { blockhash } = await connection.getLatestBlockhash("confirmed")
  transaction.recentBlockhash = blockhash
  transaction.feePayer = wallet.publicKey

  // Sign with wallet adapter
  const signedTransaction = await wallet.signTransaction(transaction)
  
  // Send and confirm
  const signature = await connection.sendRawTransaction(signedTransaction.serialize(), {
    skipPreflight: false,
    maxRetries: 3,
  })
  
  await connection.confirmTransaction(signature, "confirmed")
  
  console.log("✅ Delegation approved. Signature:", signature)
  return signature
}

/**
 * Revoke token delegation for auto-renewal
 */
export async function revokeAutoRenewalDelegation(
  connection: Connection,
  subscriber: PublicKey,
  mint: PublicKey,
  wallet: {
    publicKey: PublicKey
    signTransaction: (tx: Transaction) => Promise<Transaction>
  }
): Promise<string> {
  const { revoke } = await import("@solana/spl-token")
  
  const subscriberATA = await getAssociatedTokenAddress(mint, subscriber)
  
  console.log("🔓 Revoking auto-renewal delegation...")
  
  const signature = await revoke(
    connection,
    wallet, // Payer
    subscriberATA,
    wallet.publicKey, // Owner
    [], // Multi-signers
    undefined, // Confirmation options
    TOKEN_PROGRAM_ID
  )
  
  console.log("✅ Delegation revoked. Signature:", signature)
  return signature
}

/**
 * Update delegation amount (if subscription price changes)
 */
export async function updateDelegationAmount(
  connection: Connection,
  subscriber: PublicKey,
  mint: PublicKey,
  newAmount: number,
  wallet: {
    publicKey: PublicKey
    signTransaction: (tx: Transaction) => Promise<Transaction>
  }
): Promise<string> {
  // First revoke old delegation
  await revokeAutoRenewalDelegation(connection, subscriber, mint, wallet)
  
  // Then approve new amount
  return approveAutoRenewalDelegation(
    connection,
    subscriber,
    mint,
    newAmount,
    wallet
  )
}

